/**
 * bulletin-unsure/render — 불레틴 미확정 파트 페이지의 그리기. 순수 함수(상태 → HTML 문자열).
 *
 * 머리(renderUnsureHead) · 수리 기록(renderRepair) · 미확정 줄 카드들(renderUnsureRows) · 고침 고르기(renderFixPicker).
 * CSS 접두 `bu-` = bulletin-unsure. status 배지 · 버튼은 ③ 의 `ce-*` · 레코드 페이지의 `br-*` 를 빌린다.
 */
import { E_FIX_RESTS, E_ROW_KINDS, E_SKU_BLOCKS } from '../../api/client';
import type { DraftRow, FixBody, FixCut, FixProposal, RepairRecord, UnsureView } from '../../api/client';
import { escapeHtml, fmtDateTime } from '../../lib/cells';
import { renderBulletinStatus } from '../catalog-entry/render';
import type { BulletinUnsureData, UnsureLineKey } from './types';

// ─── 열쇠 ───────────────────────────────────────────────────────────
/** 줄 열쇠 "<table>:<row>" — chosenFixes 의 키. data-line 에도 이 글자 그대로 */
export function lineKeyOf(view: UnsureView): UnsureLineKey {
  return `${view.unsure.table}:${view.unsure.row}`;
}

// ─── 머리 ───────────────────────────────────────────────────────────
/** 머리 — 번호 · 제목 · status · unsure 수 · ③ 로 · 레코드 페이지로 링크(data-link. assembled 만) */
export function renderUnsureHead(data: BulletinUnsureData): string {
  const doc = data.doc;
  if (doc === null) return '';
  const recordsHref = `/catalog-entry/bulletin/${doc.vendor}/${doc.doc_no}/records`;
  const hasRecords = doc.status === 'assembled';   // 조립본 파일은 assembled 편에만 있다 — 그 밖은 /records 가 404

  return `
    <header class="ce-doc-head">
      <h2>${escapeHtml(doc.title === null ? '(제목 없음)' : doc.title)} <small>${escapeHtml(doc.generation === null ? '' : doc.generation)}</small></h2>
      ${renderBulletinStatus(doc.status)}
      <span class="text-muted">미확정 ${data.rows.length}줄 · ${escapeHtml(fmtDateTime(doc.updated_at))}</span>
      ${hasRecords ? `<a class="btn-ghost" data-link href="${recordsHref}">레코드 →</a>` : ''}
    </header>`;
}

// ─── 수리 기록 ──────────────────────────────────────────────────────
/** 수리 기록 — assemble_failed 편의 why + PR 링크. null 이면 '' */
export function renderRepair(repair: RepairRecord | null): string {
  if (repair === null) return '';

  return `
    <section class="bu-repair">
      <div class="bu-repair-title">수리 기록 <span class="text-muted">— 조립 코드를 고친 PR. 머지는 GitHub 에서</span></div>
      <div class="bu-repair-why">${escapeHtml(repair.why)}</div>
      <a href="${escapeHtml(repair.pr_url)}" target="_blank" rel="noopener">${escapeHtml(repair.pr_url)}</a>
    </section>`;
}

// ─── 미확정 줄 카드들 ───────────────────────────────────────────────
/** 미확정 줄들 — 줄마다 카드: 자리(표.줄 · 절) · 원문 칸 글(label · cells) · why · 제안(why · 근거 링크) · 고침 고르기 · 컨펌 버튼. 없으면 "미확정 줄이 없습니다" */
export function renderUnsureRows(data: BulletinUnsureData): string {
  if (data.rows.length === 0) return `<div class="text-muted br-note">미확정 줄이 없습니다.</div>`;

  return `<div class="bu-rows">${data.rows.map((view) => renderUnsureCard(view, data.chosenFixes[lineKeyOf(view)] ?? null)).join('')}</div>`;
}

/** 카드 하나 — 왼쪽 원문 · 오른쪽 제안과 고르기 */
function renderUnsureCard(view: UnsureView, chosenFixOrNull: (FixBody & FixCut) | null): string {
  const lineKey = lineKeyOf(view);
  const draft = view.draft;

  return `
    <article class="bu-card" data-line="${lineKey}">
      <div class="bu-place">
        <b>표 ${draft.table} · 줄 ${draft.row}</b>
        <span class="text-muted">${escapeHtml(draft.section)}</span>
        <span class="br-type">초안 ${escapeHtml(draft.kind)}</span>
      </div>
      <dl class="bu-grid">
        <dt>원문</dt>
        <dd>${renderContextTable(view)}</dd>
        <dt>왜 걸렸나</dt>
        <dd>${escapeHtml(view.unsure.why)}</dd>
        ${renderProposal(view.proposal)}
        <dt>고침</dt>
        <dd class="bu-decide">
          ${renderFixPicker(lineKey, chosenFixOrNull)}
          <button type="button" class="btn-primary" data-action="unsure-confirm" data-line="${lineKey}" ${chosenFixOrNull === null ? 'disabled' : ''}>컨펌</button>
        </dd>
      </dl>
    </article>`;
}

/** 원문 표 — 맥락 줄(회색)과 걸린 줄(굵게 · 배경)을 row 순서로. 줄마다 row 번호 · 초안 kind · 칸들 */
function renderContextTable(view: UnsureView): string {
  const rows = [...view.context, view.draft].sort((a, b) => a.row - b.row);
  const cellCount = Math.max(...rows.map((r) => r.cells.length));

  return `
    <table class="bu-context">
      <tbody>${rows.map((r) => renderContextRow(r, r.row === view.draft.row, cellCount)).join('')}</tbody>
    </table>`;
}

function renderContextRow(draft: DraftRow, isTarget: boolean, cellCount: number): string {
  const cells = Array.from({ length: cellCount }, (_, i) => draft.cells[i] ?? '');

  return `
    <tr class="${isTarget ? 'bu-context-target' : 'bu-context-near'}">
      <th>${draft.row}</th>
      <td class="bu-context-kind">${escapeHtml(draft.kind)}</td>
      ${spansOf(cells).map((span) => `<td colspan="${span.width}">${escapeHtml(span.text)}</td>`).join('')}
    </tr>`;
}

/** 옆 칸과 글이 같은 칸은 하나로 — 원문의 colspan 을 파서가 같은 글로 복사해 두어서(17246 표7 줄0). 빈 칸끼리는 안 합친다 */
function spansOf(cells: string[]): { text: string; width: number }[] {
  const spans: { text: string; width: number }[] = [];
  for (const text of cells) {
    const last = spans[spans.length - 1];
    const joinsLast = last !== undefined && text !== '' && last.text === text;
    if (joinsLast) last.width += 1;
    else spans.push({ text, width: 1 });
  }
  return spans;
}

/** 제안 — dt/dd 두 줄(제안 · 근거). fix null 이면 "조사했으나 모름", proposal null 이면 "아직 조사 안 됨" */
function renderProposal(proposal: FixProposal | null): string {
  if (proposal === null) return `<dt>제안</dt><dd class="text-muted">아직 없음 — 조사 루틴이 안 본 줄</dd>`;
  const head = proposal.fix === null ? `<b>조사했으나 모름</b>` : `<code class="bu-fix">${escapeHtml(fixLabelOf(proposal.fix))}</code>`;
  const evidence = proposal.evidence.length === 0
    ? `<dd class="text-muted">없음</dd>`
    : `<dd><ul class="bu-evidence">${proposal.evidence.map(renderEvidence).join('')}</ul></dd>`;

  return `
    <dt>제안</dt>
    <dd>${head} <span class="bu-proposal-why">${escapeHtml(proposal.why)}</span></dd>
    <dt>근거</dt>
    ${evidence}`;
}

/** 근거 하나 — 웹 주소면 링크, 우리 html(html/…) 이면 글자만(서버에 그 파일이 안 떠 있다) */
function renderEvidence(evidence: { url: string; quote: string }): string {
  const isWeb = evidence.url.startsWith('http');
  const where = isWeb
    ? `<a href="${escapeHtml(evidence.url)}" target="_blank" rel="noopener">${escapeHtml(evidence.url)}</a>`
    : `<code>${escapeHtml(evidence.url)}</code>`;

  return `<li>${where}<blockquote>${escapeHtml(evidence.quote)}</blockquote></li>`;
}

/** 고침 한 줄 요약 — "sku · cto" · "title · DL380 Gen11" · "para" */
function fixLabelOf(fix: FixBody): string {
  if (fix.kind === 'sku')   return `sku · ${fix.block}`;
  if (fix.kind === 'title') return `title · ${fix.model}`;
  return fix.kind;
}

// ─── 고침 고르기 ────────────────────────────────────────────────────
/** 고침 고르기 — kind <select>(E_ROW_KINDS) + (sku 면 block · title 이면 model) + 가르기 셋: 앞글 <input> · 둘째 제목 <input> · 나머지 <select>(없음 · rule · para).
 *  chosenFix 가 없으면 kind 미선택 · 가르기 칸 안 그림 · 컨펌 비활성 */
export function renderFixPicker(lineKey: UnsureLineKey, chosenFixOrNull: (FixBody & FixCut) | null): string {
  const kind = chosenFixOrNull === null ? '' : chosenFixOrNull.kind;
  const options = E_ROW_KINDS.map((k) => `<option value="${k}" ${k === kind ? 'selected' : ''}>${k}</option>`).join('');

  return `
    <span class="bu-picker">
      <select data-action="unsure-kind" data-line="${lineKey}">
        <option value="" ${kind === '' ? 'selected' : ''}>kind 고르기</option>
        ${options}
      </select>
      ${renderFixField(lineKey, chosenFixOrNull)}
      ${chosenFixOrNull === null ? '' : renderCutFields(lineKey, chosenFixOrNull)}
    </span>`;
}

/** 가르기 칸 셋 — data-action="unsure-field", data-field 는 text · subtitle · rest. 값이 null 이면 빈 칸 */
function renderCutFields(lineKey: UnsureLineKey, chosenFix: FixBody & FixCut): string {
  const restOptions = E_FIX_RESTS.map((r) => `<option value="${r}" ${r === chosenFix.rest ? 'selected' : ''}>${r}</option>`).join('');

  return `
    <span class="bu-cut">
      <input type="text" data-action="unsure-field" data-line="${lineKey}" data-field="text" placeholder="앞글 — 제목으로 삼을 글" value="${escapeHtml(chosenFix.text === null ? '' : chosenFix.text)}">
      <input type="text" data-action="unsure-field" data-line="${lineKey}" data-field="subtitle" placeholder="둘째 제목" value="${escapeHtml(chosenFix.subtitle === null ? '' : chosenFix.subtitle)}">
      <select data-action="unsure-field" data-line="${lineKey}" data-field="rest">
        <option value="" ${chosenFix.rest === null ? 'selected' : ''}>나머지 없음</option>
        ${restOptions}
      </select>
    </span>`;
}

/** kind 에 딸린 칸 — sku 면 block, title 이면 model. 그 밖엔 없다 */
function renderFixField(lineKey: UnsureLineKey, chosenFixOrNull: (FixBody & FixCut) | null): string {
  if (chosenFixOrNull === null) return '';
  if (chosenFixOrNull.kind === 'sku') {
    const blocks = E_SKU_BLOCKS.map((b) => `<option value="${b}" ${b === chosenFixOrNull.block ? 'selected' : ''}>${b}</option>`).join('');
    return `<select data-action="unsure-field" data-line="${lineKey}" data-field="block">${blocks}</select>`;
  }
  if (chosenFixOrNull.kind === 'title') {
    return `<input type="text" data-action="unsure-field" data-line="${lineKey}" data-field="model" placeholder="chassis_model — DL380 Gen11" value="${escapeHtml(chosenFixOrNull.model)}">`;
  }
  return '';
}
